'use client'

import { Coins, Gift, Share2, Sparkles, X } from 'lucide-react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { CopyInput } from '@/components/common/copy-input'
import { popModal } from '@/components/modals'
import { DialogContent } from '@/components/ui/dialog'
import { REFERRAL_REWARD_PERCENTAGE } from '@/lib/constant'

interface ReferralStats {
  totalReferrals: number
  creditsEarnedToday: number
  referralCode: string
}

export function ReferralModal() {
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchReferralStats = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/referral/stats')
      if (!response.ok) {
        console.error(
          'Failed to fetch referral stats:',
          response.status,
          response.statusText,
        )
        return
      }
      const data = await response.json()
      setStats(data)
    } catch (error) {
      console.error('Error fetching referral stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchReferralStats()
  }, [])

  const handleClose = () => {
    popModal()
  }

  if (isLoading) {
    return (
      <DialogContent className="sm:max-w-[500px] p-0 bg-[#18181b] border-white/10">
        <div className="relative bg-[#18181b] rounded-[32px] overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/20 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading...</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    )
  }

  if (!stats) {
    return (
      <DialogContent className="sm:max-w-[500px] p-0 bg-[#18181b] border-white/10">
        <div className="relative bg-[#18181b] rounded-[32px] overflow-hidden">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-5 right-5 z-20 text-gray-400 hover:text-white transition-colors bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full p-2"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
          <div className="p-8">
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <p className="text-gray-400 mb-4">
                  Unable to load referral information
                </p>
                <button
                  type="button"
                  onClick={fetchReferralStats}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    )
  }

  const referralUrl = `${window.location.origin}?ref=${stats.referralCode}`

  return (
    <DialogContent
      className="sm:max-w-[500px] p-0 bg-transparent border-none shadow-none"
      noCloseButton
    >
      <div className="relative bg-[#18181b] rounded-[32px] overflow-hidden shadow-2xl border border-white/10 w-full max-w-[500px] text-left animate-fade-in-up">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-5 right-5 z-20 text-gray-400 hover:text-white transition-colors bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full p-2"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {/* Top Banner Section */}
        <div className="relative bg-gradient-to-br from-[#2a1b3d] to-[#1a1a2e] p-8 pb-10">
          {/* Abstract Blob/Image Background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-purple-500/30 via-pink-500/20 to-transparent rounded-full blur-3xl pointer-events-none -translate-y-1/4 translate-x-1/4"></div>

          <div className="flex justify-between items-end relative z-10">
            <div className="max-w-[65%]">
              <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">
                Spread the love
              </h2>
              <p className="text-gray-400 text-sm font-medium">
                Earn credits on a friend's first purchase
              </p>
            </div>

            {/* Hey Boss Logo */}
            <div className="absolute right-[-20px] bottom-[-20px] w-32 h-32 flex items-center justify-center">
              <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl rotate-12 opacity-90 blur-sm absolute"></div>
              <div className="w-24 h-24 relative flex items-center justify-center">
                <Image
                  src="/v2/lightlogo2.png"
                  alt="Hey Boss"
                  width={96}
                  height={96}
                  className="w-full h-full object-contain drop-shadow-lg"
                />
                <Sparkles
                  className="absolute -top-3 -right-3 text-yellow-300 drop-shadow-lg"
                  size={24}
                  fill="currentColor"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-8 pt-6 bg-[#18181b]">
          <h3 className="text-gray-500 text-sm font-medium mb-6">
            How it works:
          </h3>

          <div className="space-y-6 mb-8">
            {/* Step 1 */}
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-zinc-800 text-white shrink-0">
                <Share2 size={20} />
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  Share your invite link
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  Send it to friends or colleagues
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-zinc-800 text-white shrink-0">
                <Gift size={20} />
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  They sign up and purchase credits
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  They can use those credits to build their first idea
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-zinc-800 text-white shrink-0">
                <Coins size={20} />
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  You earn credits equal to {REFERRAL_REWARD_PERCENTAGE * 100}% of their first purchase
                </p>
                <p className="text-gray-500 text-xs mt-0.5">
                  Earn credits after their first payment
                </p>
              </div>
            </div>
          </div>

          {/* Invite Link Section */}
          <div>
            <div className="flex justify-between items-end mb-2">
              <span className="text-sm font-medium text-gray-300">
                Your invite link:
              </span>
            </div>

            <CopyInput value={referralUrl} />
          </div>

          {/* Footer Link */}
          <div className="mt-6 text-center">
            <a
              href="https://heyboss.ai/legal/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-white underline underline-offset-2 transition-colors inline-block"
            >
              View Terms and Conditions
            </a>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
